/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isEmpty, isNone } from '@ember/utils';
import { task } from 'ember-concurrency';
import moment from 'moment-timezone';
import readFile from 'cio/utils/read-file';
import ApiService from 'cio/services/api';
import AlertsService from 'cio/services/alerts';
import PushService from 'cio/services/push';
import AnalyticsService from 'cio/services/analytics';
import { Platform } from 'cio/utils/push/platforms';
import { tracked } from '@glimmer/tracking';

interface ApnsSignature {
  Element: HTMLDivElement;
  Args: {
    isEditing?: boolean;
    platform: Platform;
    provider?: string;
    showDisablePushProviderButton: boolean;
    afterSave?: () => unknown;
    onIsDirtyChange?: (isDirty: boolean) => unknown;
    onDisablePushProvider: () => unknown;
  };
}

export default class Apns extends Component<ApnsSignature> {
  @service api!: ApiService;
  @service alerts!: AlertsService;
  @service push!: PushService;
  @service analytics!: AnalyticsService;

  @tracked isEditing = this.args.isEditing ?? false;
  @tracked errors: Record<string, any>[] = [];
  @tracked sendToSandbox = false;
  @tracked selectedFile: File | null = null;
  @tracked keyID: string | null = null;
  @tracked teamID: string | null = null;
  @tracked bundleID: string | null = null;

  constructor(owner: unknown, args: ApnsSignature['Args']) {
    super(owner, args);

    this.loadCredential.perform();
  }

  get configErrors() {
    return this.errors.filter((item) => item.type === 'config');
  }

  get sendTestErrors() {
    return this.errors.filter((item) => item.type === 'sendTest');
  }

  get isDisabled() {
    return this.push.credentials.apns == null;
  }

  get isEnabled() {
    return !this.isDisabled;
  }

  get isValid() {
    return this.validationMessage == null;
  }

  get isInvalid() {
    return !this.isValid;
  }

  get originalSendToSandbox() {
    let { platforms } = this.push;
    let platform = platforms.find((item) => item.key === 'ios');

    if (!isNone(platform)) {
      return platform.development;
    }

    return false;
  }

  get isDirty() {
    let { selectedFile, keyID, teamID, bundleID, sendToSandbox, originalSendToSandbox } = this;

    let isDirty =
      !isNone(selectedFile) ||
      !isEmpty(keyID) ||
      !isEmpty(teamID) ||
      !isEmpty(bundleID) ||
      sendToSandbox !== originalSendToSandbox;
    return isDirty;
  }

  get validationMessage() {
    let { selectedFile, keyID, teamID, bundleID } = this;

    if (isEmpty(selectedFile)) {
      return 'Upload a .p8 certificate to enable';
    }

    if (isNone(keyID) || isEmpty(keyID.trim())) {
      return "Key ID can't be empty";
    }
    if (isNone(teamID) || isEmpty(teamID.trim())) {
      return "Team ID can't be empty";
    }
    if (isNone(bundleID) || isEmpty(bundleID.trim())) {
      return "Bundle ID can't be empty";
    }

    return null;
  }

  loadCredential = task(async () => {
    let credential = await this.push.loadCredential.perform('apns');
    this.sendToSandbox = this.originalSendToSandbox;
    this.analytics.track('push', credential != null ? 'configuration_viewed' : 'setup_started', {
      platform: this.args.platform,
    });
  });

  save = task(
    {
      restartable: true,
    },
    async () => {
      let password = await this.getCredentialsFromFile.perform();

      let data = {
        credential: {
          username: this.selectedFile?.name,
          password,
          config: this.sendToSandbox ? 'sandbox' : '',
        },
      };

      try {
        let { platforms } = this.push;
        let ios = platforms.find((item) => item.key === 'ios');
        if (!isNone(ios) && ios.provider === 'fcm') {
          await this.push.updateCredentialConfig.perform('fcm', { config: '' }, false);
        }
        await this.push.createOrUpdateCredential.perform('apns', data);
      } catch (e) {
        this.updateErrors('config', e.errors);
        return false;
      }

      this.alerts.success('push.apns.enabled', 'Configuration saved!');
      this.isEditing = false;
      this.selectedFile = null;
      this.keyID = null;
      this.teamID = null;
      this.bundleID = null;

      if (this.args.afterSave != null) {
        this.args.afterSave();
      }

      return true;
    },
  );

  updateSandboxConfig = task(async (sendToSandbox: boolean) => {
    let data = {
      config: sendToSandbox ? 'sandbox' : '',
    };
    await this.push.updateCredentialConfig.perform('apns', data);

    this.alerts.success(
      `push.apns.sandbox.${sendToSandbox ? 'enabled' : 'disabled'}`,
      `Sandbox environment ${sendToSandbox ? 'enabled' : 'disabled'}`,
    );
    this.isEditing = false;
    this.sendToSandbox = sendToSandbox;
  });

  getCredentialsFromFile = task(async () => {
    let file = this.selectedFile;
    let fileContents = null;
    if (file != null) {
      fileContents = await readFile(file);
    }

    let { keyID, teamID, bundleID } = this;

    return JSON.stringify({
      key_id: keyID?.trim(),
      team_id: teamID?.trim(),
      bundle_id: bundleID?.trim(),
      private_key: fileContents,
    });
  });

  updateErrors(type: string, errors: Record<string, any>[] | undefined) {
    let currentErrors = [...this.errors];

    // clear send test errors if a config error is reported - config errors
    // supersede send test ones
    if (type === 'config') {
      currentErrors = currentErrors.filter((item) => item.type === 'config');
    }

    errors?.forEach((error) => {
      currentErrors.push({
        ...error,
        updated: moment().unix(),
        type,
      });
    });

    this.errors = [...currentErrors];
  }

  @action
  updateSendToSandbox(checked: boolean) {
    this.sendToSandbox = checked;
  }

  @action
  reset() {
    this.selectedFile = null;
    this.isEditing = false;
    this.keyID = null;
    this.teamID = null;
    this.bundleID = null;
    this.sendToSandbox = this.originalSendToSandbox;
  }

  @action
  onErrors(type: string, errors: Record<string, any>[]) {
    this.updateErrors(type, errors);
  }

  @action
  removeError(error: Record<string, any>) {
    this.errors = this.errors?.filter((errorFound) => errorFound !== error);
  }

  @action
  isDirtyChanged(_element: HTMLElement, [isDirty]: [boolean]) {
    this.args.onIsDirtyChange?.(isDirty);
  }
}
